import { $t } from '@/i18n/LocalContext'
import { createAxiosConfig, getItems, throwRequestError } from '@/request'
import getRequest from '@/request/create'
import { getDefaultZone$ } from '@/request/userServer'
import { time2str } from '@base/lang'
import appExpose from '@app/expose'
import appStore from '@app/store'
import {
  getDefaultMarketData,
  MarketData,
  Task,
  ViewTask,
} from '@app/store/market'
import { getDefaultSellData, SellData, SellRecord } from '@app/store/sell'
import {
  getDefaultLotteryData,
  LotteryPreviewData,
  getDefaultLotteryPreviewData,
  LotteryData,
  lotteryRefreshData,
  Box,
} from '@app/store/lottery'

const { config } = appExpose

const { timezoneMap } = appStore

export async function getRecord(
  zoneId: Exclude<Role['zoneId'], 0>,
  playerId: string
) {
  const $request = getRequest()

  let rspData: ViewRecordList = []
  try {
    const _res = await $request.post<RecordList>(
      `/event/${config.serverName}/user/record`,
      {},
      createAxiosConfig(playerId)
    )
    let idList: string[] = []
    const res = _res.map<RecordItem>((item) => {
      idList = idList.concat(item.rewards.map((data) => data.id))
      return {
        ...item,
      }
    }, [])
    const propMap = await getItems(Array.from(new Set(idList)))
    rspData = res.map<ViewRecordItem>((item) => {
      const map = {
        1: '圣诞庆典',
        2: '圣诞礼盒',
      }
      const alias = $t(map[item.category as keyof typeof map])

      let createdAt = time2str(
        item.date * 1000 + timezoneMap[zoneId] * 60 * 60 * 1000,
        'date-time',
        true
      )
      if (typeof createdAt === 'string' && createdAt.length > 0) {
        createdAt = createdAt.replace(' ', '<br />')
      }

      const rewards = item.rewards
        .map((data) => {
          const name = propMap[data.id] ? propMap[data.id].name : 'unknow'
          return `<span>${name}*${data.count}</span>`
        })
        .join('')

      return {
        ...item,
        alias,
        createdAt,
        rewards,
      }
    })
  } catch (error) {
    throwRequestError(error, 'record')
  }
  return rspData
}

interface CelebrationData {
  startTime: number
  endTime: number
  serverTime: number
  playEndTime: number
  gear: number
  progress: string
  nextProgress: string
  isShare: boolean
  /** 庆典币 */
  shareCoin: number
  /** 庆典值 */
  shareValue: number
  reward: { [gear: number]: RewardList }
  record?: { [key: CelebrationData['gear']]: string }
}

export async function getPreCelebrationData() {
  const $request = getRequest()
  let res: CelebrationData = {
    startTime: 0,
    endTime: 0,
    playEndTime: 0,
    serverTime: 0,
    gear: 0,
    progress: '0.00',
    nextProgress: '0.00',
    isShare: false,
    shareCoin: 0,
    shareValue: 0,
    reward: [],
  }
  try {
    const zoneId = await getDefaultZone$
    res = await $request.post(
      `/event/${config.serverName}/show/celebration`,
      {
        zoneId: String(zoneId),
      },
      createAxiosConfig()
    )
  } catch (error) {
    throwRequestError(error, 'show/celebration')
  }
  return res
}

export async function getCelebrationData(playerId: string) {
  const $request = getRequest()
  let res: CelebrationData = {
    startTime: 0,
    endTime: 0,
    playEndTime: 0,
    serverTime: 0,
    gear: 0,
    progress: '0.00',
    nextProgress: '0.00',
    isShare: false,
    shareCoin: 0,
    shareValue: 0,
    reward: [],
  }
  try {
    res = await $request.post(
      `/event/${config.serverName}/celebration/home`,
      {},
      createAxiosConfig(playerId)
    )
  } catch (error) {
    throwRequestError(error, 'celebration/home')
  }
  return res
}

export async function celebrationClaim(gear: number, playerId: string) {
  const $request = getRequest()
  let res = false
  try {
    await $request.post(
      `/event/${config.serverName}/celebration/claim`,
      {
        gear,
      },
      createAxiosConfig(playerId)
    )
    res = true
  } catch (error) {
    throwRequestError(error, 'celebration/claim')
  }
  return res
}

export async function celebrationShare(playerId: string) {
  const $request = getRequest()
  let res = false
  try {
    await $request.post(
      `/event/${config.serverName}/celebration/share`,
      {},
      createAxiosConfig(playerId)
    )
    res = true
  } catch (error) {
    throwRequestError(error, 'celebration/share')
  }
  return res
}

function createTaskDVO(data: Task): ViewTask {
  const nameMap: { [key in Task['type']]: string } = {
    1: $t('活动期间内累计消耗%{num}金币'),
    2: $t('活动期间累计在游戏内邀请并绑定玩家%{num}名'),
    3: $t('售卖圣诞树%{num}个（每日0点重置）'),
    4: $t('其他玩家在我方集市售卖圣诞树%{num}个（每日0点重置）'),
    5: $t('活动期间累计获得%{num}VIP经验'),
  }

  let statusText = [2].includes(data.type) ? $t('前往') : $t('进行中')
  if (data.status === 1) {
    statusText = $t('领取')
  } else if (data.status === 2) {
    statusText = $t('已领取')
  }

  if (data.progress > data.condition) {
    data.progress = data.condition
  }

  return {
    ...data,
    name: nameMap[data.type]
      ? nameMap[data.type].replace('%{num}', String(data.condition))
      : '',
    statusText,
  }
}

export function computeReward(tasks: Task[]): boolean {
  return tasks.some((task) => task.status === 1)
}

export async function getPreMarketData() {
  const $request = getRequest()
  let res: MarketData = getDefaultMarketData()
  try {
    const zoneId = await getDefaultZone$
    res = await $request.post(
      `/event/${config.serverName}/show/task`,
      {
        zoneId: String(zoneId),
      },
      createAxiosConfig()
    )
    res.isReward = computeReward(res.tasks)
    res.viewTasks = res.tasks.map(createTaskDVO)
  } catch (error) {
    throwRequestError(error, 'show/task')
  }
  return res
}

export async function getMarketData(playerId: string) {
  const $request = getRequest()
  let res: MarketData = getDefaultMarketData()
  try {
    res = await $request.post(
      `/event/${config.serverName}/task/home`,
      {},
      createAxiosConfig(playerId)
    )
    res.isReward = computeReward(res.tasks)
    res.viewTasks = res.tasks.map(createTaskDVO)
  } catch (error) {
    throwRequestError(error, 'task/home')
  }
  return res
}

export async function taskClaim(data: {
  playerId: string
  taskId: Task['id']
}) {
  const $request = getRequest()
  let res = true
  try {
    await $request.post(
      `/event/${config.serverName}/task/claim`,
      {
        taskID: data.taskId,
      },
      createAxiosConfig(data.playerId)
    )
  } catch (error) {
    res = false
    throwRequestError(error, 'task/claim')
  }
  return res
}

export async function getSellData(data: {
  playerId: string
  refresh?: boolean
}) {
  const $request = getRequest()
  const route = data.refresh ? 'market/refresh' : 'market/list'
  let res: SellData = getDefaultSellData()
  try {
    res = await $request.post(
      `/event/${config.serverName}/${route}`,
      {},
      createAxiosConfig(data.playerId)
    )
  } catch (error) {
    throwRequestError(error, route)
  }
  return res
}

export async function getSpecifiedSellData(data: {
  playerId: string
  targetPlayerId: string
  tree: number
}) {
  const $request = getRequest()
  const route = 'market/search'
  let res: SellData = {
    ...getDefaultSellData(),
    tree: data.tree,
  }
  try {
    const item = await $request.post<SellData['list'][number]>(
      `/event/${config.serverName}/${route}`,
      {
        playerId: data.targetPlayerId,
      },
      createAxiosConfig(data.playerId)
    )
    res.list = [item]
    res.tree = data.tree
  } catch (error) {
    throwRequestError(error, route)
  }
  return res
}

export async function sell(data: {
  playerId: string
  source: 'market' | 'search'
  uid: number
  num: number
}) {
  const $request = getRequest()
  const route = 'market/sell'
  let res: SellData['list'][number] = getDefaultSellData().list[0]
  try {
    res = await $request.post(
      `/event/${config.serverName}/${route}`,
      {
        uid: data.uid,
        number: data.num,
        source: data.source,
      },
      createAxiosConfig(data.playerId)
    )
  } catch (error) {
    throwRequestError(error, route)
  }
  return res
}

export async function getSellRecordList(playerId: string) {
  const $request = getRequest()
  const route = 'market/record'
  let res: SellRecord[] = []
  try {
    res = await $request.post(
      `/event/${config.serverName}/${route}`,
      {},
      createAxiosConfig(playerId)
    )
  } catch (error) {
    res = []
    throwRequestError(error, route)
  }
  return res
}

export async function getLotteryPreHomeData(playerId: string) {
  const $request = getRequest()
  const route = 'show/lottery'
  let res: LotteryData = getDefaultLotteryData()
  try {
    res = await $request.post(
      `/event/${config.serverName}/${route}`,
      {},
      createAxiosConfig(playerId)
    )
  } catch (error) {
    res = getDefaultLotteryData()
    throwRequestError(error, route)
  }
  let idList = []
  if (res.low.id) {
    res.low.type = 'lotteryLow'
    idList.push(res.low.id)
  }
  if (res.medium.id) {
    res.medium.type = 'lotteryMedium'
    idList.push(res.medium.id)
  }
  if (res.high.id) {
    res.high.type = 'lotteryHigh'
    idList.push(res.high.id)
  }
  const propMap = await getItems(Array.from(new Set(idList)), {
    disableWebp: true,
  })
  if (res.low.id && propMap[res.low.id]) {
    res.low.prop = propMap[res.low.id] || {}
  }
  if (res.medium.id && propMap[res.medium.id]) {
    res.medium.prop = propMap[res.medium.id] || {}
  }
  if (res.high.id && propMap[res.high.id]) {
    res.high.prop = propMap[res.high.id] || {}
  }
  return res
}
export async function getLotteryHomeData(playerId: string) {
  const $request = getRequest()
  const route = 'lottery/home'
  let res: LotteryData = getDefaultLotteryData()
  try {
    res = await $request.post(
      `/event/${config.serverName}/${route}`,
      {},
      createAxiosConfig(playerId)
    )
  } catch (error) {
    res = getDefaultLotteryData()
    throwRequestError(error, route)
  }
  let idList = []
  if (res.low.id) {
    res.low.type = 'lotteryLow'
    idList.push(res.low.id)
  }
  if (res.medium.id) {
    res.medium.type = 'lotteryMedium'
    idList.push(res.medium.id)
  }
  if (res.high.id) {
    res.high.type = 'lotteryHigh'
    idList.push(res.high.id)
  }
  const propMap = await getItems(Array.from(new Set(idList)), {
    disableWebp: true,
  })
  if (res.low.id && propMap[res.low.id]) {
    res.low.prop = propMap[res.low.id] || {}
  }
  if (res.medium.id && propMap[res.medium.id]) {
    res.medium.prop = propMap[res.medium.id] || {}
  }
  if (res.high.id && propMap[res.high.id]) {
    res.high.prop = propMap[res.high.id] || {}
  }
  return res
}

export async function lotteryPreview(playerId: string) {
  const $request = getRequest()
  const route = 'show/gift'
  let res: LotteryPreviewData = getDefaultLotteryPreviewData()
  try {
    res = await $request.post(
      `/event/${config.serverName}/${route}`,
      {},
      createAxiosConfig(playerId)
    )
  } catch (error) {
    res = getDefaultLotteryPreviewData()
    throwRequestError(error, route)
  }
  let idList: string[] = []
  let key = ['low', 'medium', 'high'] as Array<keyof LotteryPreviewData>
  key.forEach((k) => {
    res[k].forEach((item) => {
      idList.push(item.id)
    })
  })
  const propMap = await getItems(Array.from(new Set(idList)))
  let setProp = (item: any) => {
    item.prop = {}
    if (item.id && propMap[item.id]) {
      item.prop = propMap[item.id] || {}
    }
    return item
  }
  const sortFunc = (arr: Box[]) =>
    arr.sort((a, b) => {
      let res = 0
      if (a.quality - b.quality < 0) {
        res = 1
      } else if (a.quality - b.quality > 0) {
        res = -1
      } else {
        const sub = a.coin - b.coin
        res = sub === 0 ? 0 : sub < 0 ? 1 : -1
      }
      return res
    })
  res.low = sortFunc(res.low.map(setProp))
  res.medium = sortFunc(res.medium.map(setProp))
  res.high = sortFunc(res.high.map(setProp))
  return res
}
export async function lotteryRefresh({
  playerId,
  gear,
}: {
  playerId: string
  gear: string
}) {
  const $request = getRequest()
  const route = 'lottery/refresh'
  let res: lotteryRefreshData = {
    coin: 0,
    items: {
      type: 'lotteryLow',
      coin: 0,
      count: 0,
      id: '',
      quality: 1,
    },
  }
  try {
    res = await $request.post(
      `/event/${config.serverName}/${route}`,
      {
        gear,
      },
      createAxiosConfig(playerId)
    )
  } catch (error) {
    res = {
      coin: 0,
      items: {} as any,
    }
    throwRequestError(error, route)
  }
  let idList = []
  console.log(res)
  idList.push(res.items.id)
  const propMap = await getItems(Array.from(new Set(idList)), {
    disableWebp: true,
  })
  if (res.items.id && propMap[res.items.id]) {
    res.items.prop = propMap[res.items.id] || {}
  }
  return res
}
export async function lotteryClaim({
  playerId,
  gear,
}: {
  playerId: string
  gear: string
}) {
  const $request = getRequest()
  const route = 'lottery/claim'
  let res: lotteryRefreshData = {
    coin: 0,
    items: {
      type: 'lotteryLow',
      coin: 0,
      count: 0,
      id: '',
      quality: 1,
    },
  }
  try {
    res = await $request.post(
      `/event/${config.serverName}/${route}`,
      {
        gear,
      },
      createAxiosConfig(playerId)
    )
  } catch (error) {
    res = {
      coin: 0,
      items: {} as any,
    }
    throwRequestError(error, route)
  }
  let idList = []
  idList.push(res.items.id)
  const propMap = await getItems(Array.from(new Set(idList)))
  if (res.items.id && propMap[res.items.id]) {
    res.items.prop = propMap[res.items.id] || {}
  }
  return res
}
